import { create } from 'zustand';
import { auth, db } from '../firebase';
import { collection, doc, setDoc, onSnapshot, getDocs, query, where, updateDoc, arrayUnion, deleteDoc, getDoc, Timestamp, increment, orderBy, limit } from 'firebase/firestore';
import { createBag, Tile } from '../utils/tiles';
import { createEmptyBoard, getBoardLayout, isCompleteSquareMatrix, normalizeBoardLayout, parseMatrix } from '../utils/board';
import { GAME_MODES, GameModeKey } from '../constants/gameModes';
import { APP_RULES_VERSION } from '../constants';
import { applyDynamicBoardIfNeeded } from '../utils/dynamicBoard';
import { useStatsStore } from './useStatsStore';
import { GameTurn, GameRecord } from '../types';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';
import { ExchangeMode, GameState } from '../types/GameState';
import { getExchangeMode, getLegacySwapsAllowedForExchangeMode, getTileExchangeEndsTurn, getTileExchangeMessage } from '../utils/tileExchange';
import { getPublicCoachId, getVirtualRivalEngineProfile } from '../constants/virtualRivals';
export type { GameState };

function cleanObject(obj: any) {
  // Recursively remove undefined values
  const newObj: any = Array.isArray(obj) ? [] : {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value === undefined) return;
    
    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      newObj[key] = cleanObject(value);
    } else {
      newObj[key] = value;
    }
  });
  return newObj;
}

const usesCustomBoardLayout = (options: Partial<GameState>) => (
  options.boardCategory === 'PERSONAL'
    || options.boardCategory === 'COMMUNITY'
    || Boolean(options.customLayout && (options.boardType?.startsWith('PERSONAL') || options.boardType?.startsWith('COMMUNITY')))
);

const getCustomBoardSize = (customLayout: unknown): number | null => {
  const parsedLayout = parseMatrix(customLayout);
  return isCompleteSquareMatrix(parsedLayout) ? parsedLayout.length : null;
};

const resolveGameBoardConfig = (options: Partial<GameState>, defaultBoardSize: number, defaultBoardType: string, multiplierDensity: string) => {
  const boardType = options.boardType || defaultBoardType;
  const customBoardSize = usesCustomBoardLayout(options) ? getCustomBoardSize(options.customLayout) : null;
  const boardSize = customBoardSize || defaultBoardSize;
  const fallbackLayout = getBoardLayout(boardType, boardSize, multiplierDensity as any);
  const boardLayout = usesCustomBoardLayout(options)
    ? normalizeBoardLayout(options.customLayout, boardSize, fallbackLayout)
    : fallbackLayout;

  return {
    boardType,
    boardSize,
    boardLayout,
    serializedCustomLayout: usesCustomBoardLayout(options) ? JSON.stringify(boardLayout) : null
  };
};

const getGameBoardSize = (game: Pick<GameState, 'board' | 'customLayout' | 'config'>) => {
  const customLayout = parseMatrix(game.customLayout);
  if (isCompleteSquareMatrix(customLayout)) return customLayout.length;

  const board = parseMatrix(game.board);
  if (isCompleteSquareMatrix(board)) return board.length;

  return game.config?.boardSize || 15;
};

const resolveGameForRenderBoardSize = (game: Partial<GameState>) => {
  const customLayout = parseMatrix(game.customLayout);
  if (isCompleteSquareMatrix(customLayout)) return customLayout.length;

  const board = parseMatrix(game.board);
  if (isCompleteSquareMatrix(board)) return board.length;

  const modeConfig = game.mode ? GAME_MODES[game.mode] : undefined;
  return game.config?.boardSize || modeConfig?.boardSize || 15;
};

const resolveExchangeSettings = (options: Partial<GameState>): { exchangeMode: ExchangeMode; swapsAllowed: number } => {
  const exchangeMode = (options.exchangeMode || options.swapsAllowed !== undefined)
    ? getExchangeMode(options)
    : 'exchange_and_continue';
  return {
    exchangeMode,
    swapsAllowed: getLegacySwapsAllowedForExchangeMode(exchangeMode)
  };
};

const normalizeTileBoard = (rawBoard: unknown, size: number) => {
  const source = parseMatrix(rawBoard);
  const normalized = createEmptyBoard<Tile>(size);

  if (!Array.isArray(source)) return normalized;

  for (let r = 0; r < size; r++) {
    const sourceRow = source[r];
    if (!Array.isArray(sourceRow)) continue;
    for (let c = 0; c < size; c++) {
      normalized[r][c] = (sourceRow[c] as Tile | null) || null;
    }
  }

  return normalized;
};

const normalizeGameForRender = (rawGame: Partial<GameState> & { id?: string }): GameState => {
  const mode = rawGame.mode && GAME_MODES[rawGame.mode] ? rawGame.mode : 'clasica';
  const modeConfig = GAME_MODES[mode];
  const config = { ...modeConfig, ...(rawGame.config || {}) };
  const boardSize = resolveGameForRenderBoardSize({ ...rawGame, mode, config });
  const boardType = rawGame.boardType || config.boardType || 'VERBO';
  const multiplierDensity = config.multiplierDensity || 'full';
  const fallbackLayout = getBoardLayout(boardType, boardSize, multiplierDensity as any);
  const shouldUseCustomLayout = usesCustomBoardLayout(rawGame);
  const customLayout = shouldUseCustomLayout
    ? JSON.stringify(normalizeBoardLayout(rawGame.customLayout, boardSize, fallbackLayout))
    : rawGame.customLayout;

  return {
    ...rawGame,
    mode,
    config: { ...config, boardSize },
    boardType,
    board: JSON.stringify(normalizeTileBoard(rawGame.board, boardSize)),
    customLayout
  } as GameState;
};

interface GameStore {
  currentGame: GameState | null;
  activeGames: GameState[];
  activeGamesLoaded: boolean;
  activeGamesUserId: string | null;
  loading: boolean;
  createGame: (userId: string, options?: Partial<GameState>) => Promise<string>;
  createBotGame: (userId: string, options?: Partial<GameState> & { botDifficulty?: 'axel' | 'nilo' | 'orion' | 'zenda' }) => Promise<string>;
  createDirectGame: (userId: string, opponentId: string, options?: Partial<GameState>) => Promise<string>;
  joinGame: (userId: string, gameId: string) => Promise<void>;
  quickPlay: (userId: string, options?: any) => Promise<{ gameId: string, created: boolean }>;
  cancelGame: (gameId: string, userId: string) => Promise<void>;
  subscribeToGame: (gameId: string) => () => void;
  fetchActiveGames: (userId: string) => () => void;
  fetchGameHistory: (userId: string) => Promise<GameState[]>;
  commitMove: (gameId: string, userId: string, placedTiles: { row: number, col: number, tile: Tile }[], calculatedScore?: number, timeRemaining?: number, wordsPlayed?: string[], validWords?: string[], invalidWords?: string[], premiumSquaresUsed?: string[], longestWordLength?: number, scoreBreakdown?: any) => Promise<void>;
  passTurn: (gameId: string, userId: string, timeRemaining?: number) => Promise<void>;
  resignGame: (gameId: string, userId: string) => Promise<void>;
  handleTimeout: (gameId: string, userId: string) => Promise<void>;
  abandonGame: (gameId: string, userId: string) => Promise<void>;
  shuffleRack: (gameId: string, userId: string) => Promise<void>;
  toggleTilePin: (gameId: string, userId: string, tileId: string) => Promise<void>;
  swapTiles: (gameId: string, userId: string, tileIds: string[], timeRemaining?: number) => Promise<void>;
  setLanguage: (gameId: string, language: 'es' | 'en' | 'fr' | 'ca') => Promise<void>;
}

export const useGameStore = create<GameStore>((set, get) => ({
  currentGame: null,
  activeGames: [],
  activeGamesLoaded: false,
  activeGamesUserId: null,
  loading: false,

  createGame: async (userId, options = {}) => {
    try {
      const gameId = doc(collection(db, 'games')).id;
      
      const mode = (options.mode as GameModeKey) || 'clasica';
      const config = GAME_MODES[mode];
      const bag = createBag(config.tileCount);
      const exchangeSettings = resolveExchangeSettings(options);
      
      const multiplierDensity = config.multiplierDensity || 'full';
      const { boardType, boardSize, serializedCustomLayout } = resolveGameBoardConfig(
        options,
        config.boardSize || 15,
        (config as any).boardType || 'VERBO',
        multiplierDensity
      );
      
      // Draw tiles for creator
      const rack = bag.splice(0, config.rackSize);
      
      const timeLimit = options.timeLimit || null;
      
      const playerDetails: Record<string, any> = {
        [userId]: { score: 0, rack }
      };
      
      if (timeLimit) {
        playerDetails[userId].timeRemaining = timeLimit;
      }

      const newGame: GameState = {
        id: gameId,
        players: [userId],
        playerDetails,
        board: JSON.stringify(createEmptyBoard(boardSize)),
        bag,
        currentTurn: userId,
        status: 'waiting',
        mode,
        config,
        turnCount: 0,
        boardHistory: [],
        freedCells: [],
        language: options.language || 'es',
        rulesVersion: APP_RULES_VERSION,
        dictionaryMode: options.dictionaryMode || 'standard',
        boardType: boardType,
        boardCategory: options.boardCategory,
        boardName: options.boardName,
        boardCreatorName: options.boardCreatorName,
        isCopiedBoard: options.isCopiedBoard,
        customLayout: serializedCustomLayout,
        coachMode: options.coachMode,
        coachId: getPublicCoachId(options.coachMode),
        experienceMode: options.experienceMode,
        boardStyle: options.boardStyle,
        precisionMode: options.precisionMode,
        exchangeMode: exchangeSettings.exchangeMode,
        swapsAllowed: exchangeSettings.swapsAllowed,
        swapsUsed: { [userId]: 0 },
        timeMode: options.timeMode || 'unlimited',
        turnTimeSeconds: options.turnTimeSeconds || null,
        timeLimit,
        turns: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'games', gameId), cleanObject(newGame));
      return gameId;
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'games');
      throw error;
    }
  },

  joinGame: async (userId, gameId) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const docSnap = await getDoc(gameRef);
      
      if (!docSnap.exists()) {
        throw new Error("Game not found");
      }
      
      const gameData = docSnap.data() as GameState;
      
      if (gameData.players.includes(userId)) {
        return; // Already joined
      }
      
      if (gameData.status !== 'waiting') {
        throw new Error("Game is not waiting for players");
      }
      
      const newBag = [...gameData.bag];
      const newRack = newBag.splice(0, gameData.config.rackSize);
      
      const timeLimit = gameData.timeLimit;
      
      const newPlayerDetails: any = { score: 0, rack: newRack };
      if (timeLimit) {
        newPlayerDetails.timeRemaining = timeLimit;
      }
      
      await updateDoc(gameRef, {
        players: arrayUnion(userId),
        [`playerDetails.${userId}`]: newPlayerDetails,
        [`swapsUsed.${userId}`]: 0,
        bag: newBag,
        status: 'active',
        startedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  createBotGame: async (userId, options = {}) => {
    try {
      console.log(`[GameStore] Creating bot game for user ${userId}...`);
      const gameId = doc(collection(db, 'games')).id;
      const mode = (options.mode as GameModeKey) || 'clasica';
      const config = GAME_MODES[mode];
      const bag = createBag(config.tileCount);
      const exchangeSettings = resolveExchangeSettings(options);
      
      const multiplierDensity = config.multiplierDensity || 'full';
      const { boardType, boardSize, serializedCustomLayout } = resolveGameBoardConfig(
        options,
        config.boardSize || 15,
        (config as any).boardType || 'VERBO',
        multiplierDensity
      );
      
      const rack1 = bag.splice(0, config.rackSize);
      const rack2 = bag.splice(0, config.rackSize);
      
      const timeLimit = options.timeLimit || null;
      
      const playerDetails: Record<string, any> = {
        [userId]: { score: 0, rack: rack1 },
        ['ROBOT']: { score: 0, rack: rack2 }
      };
      
      if (timeLimit) {
        playerDetails[userId].timeRemaining = timeLimit;
        playerDetails['ROBOT'].timeRemaining = timeLimit;
      }
      
      const newGame: GameState = {
        id: gameId,
        players: [userId, 'ROBOT'],
        playerDetails,
        board: JSON.stringify(createEmptyBoard(boardSize)),
        bag,
        currentTurn: userId,
        status: 'active',
        mode,
        config,
        turnCount: 0,
        boardHistory: [],
        freedCells: [],
        language: options.language || 'es',
        rulesVersion: APP_RULES_VERSION,
        dictionaryMode: options.dictionaryMode || 'standard',
        boardType: boardType,
        boardCategory: options.boardCategory,
        boardName: options.boardName,
        boardCreatorName: options.boardCreatorName,
        boardId: options.boardId,
        isCopiedBoard: options.isCopiedBoard,
        customLayout: serializedCustomLayout,
        coachMode: options.coachMode,
        coachId: getPublicCoachId(options.coachMode) as any,
        experienceMode: options.experienceMode,
        boardStyle: options.boardStyle,
        precisionMode: options.precisionMode,
        exchangeMode: exchangeSettings.exchangeMode,
        swapsAllowed: exchangeSettings.swapsAllowed,
        swapsUsed: { [userId]: 0, 'ROBOT': 0 },
        timeMode: options.timeMode || 'unlimited',
        turnTimeSeconds: options.turnTimeSeconds || null,
        timeLimit,
        botDifficulty: getVirtualRivalEngineProfile(options.botDifficulty as any),
        turns: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if ((options.boardCategory === 'PERSONAL' || options.boardCategory === 'COMMUNITY') && options.boardId) {
        const boardRef = doc(db, 'personalBoards', options.boardId);
        updateDoc(boardRef, {
          timesPlayed: increment(1)
        }).catch(err => console.error('Error incrementing timesPlayed:', err));
      }

      await setDoc(doc(db, 'games', gameId), cleanObject(newGame));
      return gameId;
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'games');
      throw error;
    }
  },

  createDirectGame: async (userId, opponentId, options = {}) => {
    try {
      console.log(`[GameStore] Creating direct game between ${userId} and ${opponentId}...`);
      const gameId = doc(collection(db, 'games')).id;
      const mode = (options.mode as GameModeKey) || 'clasica';
      const config = GAME_MODES[mode];
      const bag = createBag(config.tileCount);
      const exchangeSettings = resolveExchangeSettings(options);
      
      const multiplierDensity = config.multiplierDensity || 'full';
      const { boardType, boardSize, serializedCustomLayout } = resolveGameBoardConfig(
        options,
        config.boardSize || 15,
        (config as any).boardType || 'VERBO',
        multiplierDensity
      );
      
      const rack1 = bag.splice(0, config.rackSize);
      const rack2 = bag.splice(0, config.rackSize);
      
      const timeLimit = options.timeLimit || null;
      
      const playerDetails: Record<string, any> = {
        [userId]: { score: 0, rack: rack1 },
        [opponentId]: { score: 0, rack: rack2 }
      };
      
      if (timeLimit) {
        playerDetails[userId].timeRemaining = timeLimit;
        playerDetails[opponentId].timeRemaining = timeLimit;
      }
      
      const newGame: GameState = {
        id: gameId,
        players: [userId, opponentId],
        playerDetails,
        board: JSON.stringify(createEmptyBoard(boardSize)),
        bag,
        currentTurn: userId,
        status: 'active',
        mode,
        config,
        turnCount: 0,
        boardHistory: [],
        freedCells: [],
        language: options.language || 'es',
        rulesVersion: APP_RULES_VERSION,
        dictionaryMode: options.dictionaryMode || 'standard',
        boardType: boardType,
        boardCategory: options.boardCategory,
        boardName: options.boardName,
        boardCreatorName: options.boardCreatorName,
        boardId: options.boardId,
        isCopiedBoard: options.isCopiedBoard,
        customLayout: serializedCustomLayout,
        coachMode: options.coachMode,
        coachId: getPublicCoachId(options.coachMode),
        experienceMode: options.experienceMode,
        boardStyle: options.boardStyle,
        precisionMode: options.precisionMode,
        exchangeMode: exchangeSettings.exchangeMode,
        swapsAllowed: exchangeSettings.swapsAllowed,
        swapsUsed: { [userId]: 0, [opponentId]: 0 },
        timeMode: options.timeMode || 'unlimited',
        turnTimeSeconds: options.turnTimeSeconds || null,
        timeLimit,
        turns: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if ((options.boardCategory === 'PERSONAL' || options.boardCategory === 'COMMUNITY') && options.boardId) {
        const boardRef = doc(db, 'personalBoards', options.boardId);
        updateDoc(boardRef, {
          timesPlayed: increment(1)
        }).catch(err => console.error('Error incrementing timesPlayed:', err));
      }

      await setDoc(doc(db, 'games', gameId), cleanObject(newGame));
      return gameId;
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'games');
      throw error;
    }
  },

  quickPlay: async (userId: string, options: any = {}) => {
    if (!auth.currentUser) {
      console.warn('quickPlay called but auth.currentUser is null');
      throw new Error('Debes iniciar sesión para jugar');
    }

    try {
      const exchangeSettings = resolveExchangeSettings(options);
      // Find a waiting game with exact matching parameters
      const q = query(
        collection(db, 'games'), 
        where('status', '==', 'waiting'),
        where('mode', '==', options.mode || 'clasica'),
        where('language', '==', options.language || 'es'),
        where('dictionaryMode', '==', options.dictionaryMode || 'standard'),
        where('boardType', '==', options.boardType || 'VERBO'),
        where('swapsAllowed', '==', exchangeSettings.swapsAllowed),
        where('coachMode', '==', options.coachMode ?? false),
        where('timeMode', '==', options.timeMode || 'unlimited'),
        where('turnTimeSeconds', '==', options.turnTimeSeconds || null),
        where('timeLimit', '==', options.timeLimit ?? null)
      );
      const snapshot = await getDocs(q);
      
      const waitingGames = snapshot.docs.filter(d => !d.data().players.includes(userId));
      
      if (waitingGames.length > 0) {
        const gameDoc = waitingGames[0];
        const gameData = gameDoc.data() as GameState;
        
        // Draw tiles from the bag
        const newBag = [...gameData.bag];
        const newRack = newBag.splice(0, gameData.config?.rackSize || 7);
        
        const timeLimit = gameData.timeLimit;
        
        const newPlayerDetails: any = { score: 0, rack: newRack };
        if (timeLimit) {
          newPlayerDetails.timeRemaining = timeLimit;
        }
        
        try {
          await updateDoc(doc(db, 'games', gameDoc.id), {
            players: arrayUnion(userId),
            [`playerDetails.${userId}`]: newPlayerDetails,
            [`swapsUsed.${userId}`]: 0,
            bag: newBag,
            status: 'active',
            updatedAt: new Date().toISOString()
          });
          return { gameId: gameDoc.id, created: false };
        } catch (updateError: any) {
          // If update fails (e.g., due to race condition where someone else joined),
          // we can try creating a new game instead or retrying.
          // For simplicity, we'll just fall back to creating a new game.
          console.warn("Failed to join waiting game, creating a new one instead.", updateError);
          const gameId = await get().createGame(userId, options);
          return { gameId, created: true };
        }
      } else {
        // Create new game
        const gameId = await get().createGame(userId, options);
        return { gameId, created: true };
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'games');
      throw error;
    }
  },

  cancelGame: async (gameId, userId) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const docSnap = await getDoc(gameRef);
      if (docSnap.exists()) {
        const data = docSnap.data() as GameState;
        if (data.status === 'waiting' && data.players.includes(userId)) {
          try {
            await deleteDoc(gameRef);
          } catch (deleteError) {
            console.warn("Failed to delete game, it might have become active.", deleteError);
          }
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `games/${gameId}`);
    }
  },

  subscribeToGame: (gameId) => {
    try {
      // Track processed games in this subscription instance to avoid redundant stats processing
      let hasProcessedStats = false;

      const unsubscribe = onSnapshot(doc(db, 'games', gameId), (docSnap) => {
        if (docSnap.exists()) {
          const data = normalizeGameForRender({ id: docSnap.id, ...docSnap.data() });
          const previousGame = get().currentGame;
          set({ currentGame: data });

          // Trigger stats processing if game just finished or was abandoned
          const isFinished = data.status === 'finished' || data.status === 'abandoned';
          const wasNotFinished = !previousGame || (previousGame.status !== 'finished' && previousGame.status !== 'abandoned');
          
          if (isFinished && wasNotFinished && !hasProcessedStats) {
            hasProcessedStats = true;
            useStatsStore.getState().processGame(convertToGameRecord(data));
          }
        }
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, `games/${gameId}`);
      });
      return unsubscribe;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `games/${gameId}`);
      return () => {};
    }
  },

  commitMove: async (gameId: string, userId: string, placedTiles: { row: number, col: number, tile: Tile }[], calculatedScore?: number, timeRemaining?: number, wordsPlayed: string[] = [], validWords: string[] = [], invalidWords: string[] = [], premiumSquaresUsed: string[] = [], longestWordLength: number = 0, scoreBreakdown?: any) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state) return;

      const currentBoard = normalizeTileBoard(state.board, getGameBoardSize(state));
      const newBoard = currentBoard.map((row: any) => [...row]);
      let moveScore = calculatedScore ?? 0;

      placedTiles.forEach(({ row, col, tile }) => {
        newBoard[row][col] = { ...tile, placedTurn: state.turnCount };
        if (calculatedScore === undefined) {
          moveScore += tile.points; // Fallback basic scoring
        }
      });

      // Bingo bonus fallback
      if (calculatedScore === undefined && placedTiles.length === (state.config?.rackSize || 7)) {
        moveScore += 40;
      }

      const myDetails = state.playerDetails[userId];
      
      // Defensive check: Ensure no duplicate IDs in the move
      const placedIds = new Set(placedTiles.map(p => p.tile.id));
      if (placedIds.size !== placedTiles.length) {
        console.error("CRITICAL: Duplicate tile IDs detected in placedTiles", placedTiles);
        throw new Error("Error de consistencia: Fichas duplicadas detectadas.");
      }

      // Defensive check: Ensure all placed tiles exist in the player's rack
      const missingTiles = placedTiles.filter(pt => !myDetails.rack.some(rt => rt.id === pt.tile.id));
      if (missingTiles.length > 0) {
        console.error("CRITICAL: Placed tiles not found in player rack", { missingTiles, rack: myDetails.rack });
        throw new Error("Error de consistencia: Intento de jugar fichas que no están en el atril.");
      }

      const newRack = myDetails.rack.filter(rt => !placedTiles.some(pt => pt.tile.id === rt.id));
      
      // Draw new tiles
      const newBag = [...state.bag];
      const maxRackSize = state.config?.rackSize || 7;
      const tilesNeeded = maxRackSize - newRack.length;
      
      // Defensive check: Ensure we don't exceed max rack size
      if (newRack.length + placedTiles.length > maxRackSize) {
        console.error("CRITICAL: Total tiles exceed max rack size", { rack: newRack.length, placed: placedTiles.length, max: maxRackSize });
      }

      const drawnTiles = newBag.splice(0, tilesNeeded);
      newRack.push(...drawnTiles);

      // Final defensive check: Unique IDs in new rack
      const finalRackIds = new Set();
      const deduplicatedRack: Tile[] = [];
      newRack.forEach(t => {
        if (!finalRackIds.has(t.id)) {
          deduplicatedRack.push(t);
          finalRackIds.add(t.id);
        }
      });

      if (deduplicatedRack.length !== newRack.length) {
        console.error("CRITICAL: Duplicate tile IDs detected and removed from new rack after drawing", {
          original: newRack.length,
          deduplicated: deduplicatedRack.length
        });
      }
      
      const finalRack = deduplicatedRack;

      const opponentId = state.players.find(id => id !== userId) || userId;

      // Dynamic Board Logic
      const dynamicUpdate = await applyDynamicBoardIfNeeded({ ...state, board: newBoard, turnCount: (state.turnCount || 0) + 1 } as unknown as GameState);
      
      const newTurn: GameTurn = {
        turnNumber: state.turnCount + 1,
        playerId: userId,
        startedAt: Timestamp.fromDate(new Date(state.updatedAt)),
        finishedAt: Timestamp.now(),
        durationSec: (Date.now() - new Date(state.updatedAt).getTime()) / 1000,
        actionType: 'play',
        wordsPlayed,
        validWords,
        invalidWords,
        lettersPlaced: placedTiles.map(p => p.tile.letter),
        lettersCount: placedTiles.length,
        score: moveScore,
        scoreBreakdown,
        usedAllRack: placedTiles.length === (state.config?.rackSize || 7),
        usedPremium: premiumSquaresUsed.length > 0,
        premiumSquaresUsed,
        longestWordLength,
        openedBoard: placedTiles.length >= 5,
        blockedBoard: false
      };

      const updateData: any = {
        board: JSON.stringify(dynamicUpdate?.board || newBoard),
        bag: newBag,
        [`playerDetails.${userId}.score`]: myDetails.score + moveScore + (dynamicUpdate ? ((state.config as any)?.connectionBonus || 0) : 0),
        [`playerDetails.${userId}.rack`]: finalRack,
        currentTurn: opponentId,
        consecutivePasses: 0,
        turnCount: (state.turnCount || 0) + 1,
        turns: arrayUnion(newTurn),
        lastMove: {
          userId,
          type: 'move',
          placedTiles,
          words: validWords,
          score: moveScore,
          timestamp: new Date().toISOString(),
          id: Math.random().toString(36).substring(7)
        },
        updatedAt: new Date().toISOString()
      };
      
      if (dynamicUpdate?.freedCells) {
        updateData.freedCells = dynamicUpdate.freedCells;
      }

      if (timeRemaining !== undefined) {
        updateData[`playerDetails.${userId}.timeRemaining`] = timeRemaining;
      }

      // Check for game end condition: empty rack and empty bag
      if (finalRack.length === 0 && newBag.length === 0) {
        updateData.status = 'finished';
        updateData.endReason = 'normal';
        
        const oppDetails = state.playerDetails[opponentId];
        if (oppDetails) {
          const oppRemainingPoints = oppDetails.rack.reduce((sum, t) => sum + t.points, 0);
          updateData[`playerDetails.${userId}.score`] += oppRemainingPoints;
          updateData[`playerDetails.${opponentId}.score`] = Math.max(0, oppDetails.score - oppRemainingPoints);
          
          const finalMyScore = updateData[`playerDetails.${userId}.score`];
          const finalOppScore = updateData[`playerDetails.${opponentId}.score`];
          
          updateData.winner = finalMyScore > finalOppScore ? userId : (finalOppScore > finalMyScore ? opponentId : 'draw');
          updateData.loser = finalMyScore > finalOppScore ? opponentId : (finalOppScore > finalMyScore ? userId : undefined);
          updateData.isDraw = finalMyScore === finalOppScore;
          updateData.finishedAt = new Date().toISOString();
        }
      }

      await updateDoc(gameRef, updateData);

      // Update player activity tracker
      try {
        const { useAuthStore } = await import('./useAuthStore');
        await useAuthStore.getState().updateActivity();
      } catch (err) {
        console.warn("[GameStore] Error updating activity:", err);
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  passTurn: async (gameId: string, userId: string, timeRemaining?: number) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state) return;

      const opponentId = state.players.find(id => id !== userId) || userId;
      const newPasses = (state.consecutivePasses || 0) + 1;
      
      const newTurn: GameTurn = {
        turnNumber: state.turnCount + 1,
        playerId: userId,
        startedAt: Timestamp.fromDate(new Date(state.updatedAt)),
        finishedAt: Timestamp.now(),
        durationSec: (Date.now() - new Date(state.updatedAt).getTime()) / 1000,
        actionType: 'pass',
        wordsPlayed: [],
        validWords: [],
        invalidWords: [],
        lettersPlaced: [],
        lettersCount: 0,
        score: 0,
        usedAllRack: false,
        usedPremium: false,
        premiumSquaresUsed: [],
        longestWordLength: 0,
        openedBoard: false,
        blockedBoard: false
      };

      const updateData: any = {
        currentTurn: opponentId,
        consecutivePasses: newPasses,
        turnCount: (state.turnCount || 0) + 1,
        turns: arrayUnion(newTurn),
        lastMove: {
          userId,
          type: 'pass',
          score: 0,
          timestamp: new Date().toISOString(),
          id: Math.random().toString(36).substring(7)
        },
        updatedAt: new Date().toISOString()
      };
      
      if (timeRemaining !== undefined) {
        updateData[`playerDetails.${userId}.timeRemaining`] = timeRemaining;
      }

      // End game if too many consecutive passes (e.g., 4)
      if (newPasses >= 4) {
        updateData.status = 'finished';
        updateData.endReason = 'normal';
        
        // Determine winner based on score
        const myScore = state.playerDetails[userId]?.score || 0;
        const oppScore = state.playerDetails[opponentId]?.score || 0;
        
        updateData.winner = myScore > oppScore ? userId : (oppScore > myScore ? opponentId : 'draw');
        updateData.loser = myScore > oppScore ? opponentId : (oppScore > myScore ? userId : undefined);
        updateData.isDraw = myScore === oppScore;
        updateData.finishedAt = new Date().toISOString();
      }

      await updateDoc(gameRef, updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  resignGame: async (gameId: string, userId: string) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state) return;

      const opponentId = state.players.find(id => id !== userId) || userId;

      const updateData: any = {
        status: 'finished',
        winner: opponentId,
        loser: userId,
        isDraw: false,
        endReason: 'resignation',
        finishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(gameRef, updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  handleTimeout: async (gameId, userId) => {
    try {
      const state = get().currentGame;
      if (!state) return;
      const opponentId = state.players.find(id => id !== userId) || userId;

      const updateData: any = {
        status: 'finished',
        winner: opponentId,
        loser: userId,
        isDraw: false,
        endReason: 'timeout',
        finishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'games', gameId), updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  abandonGame: async (gameId, userId) => {
    try {
      const state = get().currentGame;
      if (!state) return;
      const opponentId = state.players.find(id => id !== userId) || userId;

      const updateData: any = {
        status: 'abandoned',
        winner: opponentId,
        loser: userId,
        isDraw: false,
        endReason: 'abandon',
        finishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'games', gameId), updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  shuffleRack: async (gameId: string, userId: string) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state) return;

      const fullRack = [...state.playerDetails[userId].rack];
      
      // Separate pinned and unpinned tiles
      const unpinnedTiles = fullRack.filter(t => !t.isPinned);
      const pinnedTilesWithIndices = fullRack.map((t, i) => ({ t, i })).filter(item => item.t.isPinned);

      if (unpinnedTiles.length <= 1) return; // Nothing to shuffle

      // Fisher-Yates shuffle on unpinned tiles
      for (let i = unpinnedTiles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [unpinnedTiles[i], unpinnedTiles[j]] = [unpinnedTiles[j], unpinnedTiles[i]];
      }

      // Reconstruct the rack
      const newRack: Tile[] = [];
      let unpinnedIdx = 0;
      for (let i = 0; i < fullRack.length; i++) {
        const pinned = pinnedTilesWithIndices.find(p => p.i === i);
        if (pinned) {
          newRack.push(pinned.t);
        } else {
          newRack.push(unpinnedTiles[unpinnedIdx++]);
        }
      }

      await updateDoc(gameRef, {
        [`playerDetails.${userId}.rack`]: newRack
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  toggleTilePin: async (gameId: string, userId: string, tileId: string) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state) return;

      const rack = [...state.playerDetails[userId].rack];
      const tileIdx = rack.findIndex(t => t.id === tileId);
      if (tileIdx === -1) return;

      rack[tileIdx] = {
        ...rack[tileIdx],
        isPinned: !rack[tileIdx].isPinned
      };

      await updateDoc(gameRef, {
        [`playerDetails.${userId}.rack`]: rack
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  swapTiles: async (gameId: string, userId: string, tileIds: string[], timeRemaining?: number) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const state = get().currentGame;
      if (!state || tileIds.length === 0) return;

      const tileExchangeMessage = getTileExchangeMessage(state, userId, tileIds.length);
      if (!tileExchangeMessage.canExchange) {
        throw new Error(tileExchangeMessage.body);
      }

      const myDetails = state.playerDetails[userId];
      const tilesToSwap = myDetails.rack.filter(t => tileIds.includes(t.id));
      const remainingRack = myDetails.rack.filter(t => !tileIds.includes(t.id));
      
      // Defensive check: Ensure we are swapping tiles that actually exist in the rack
      if (tilesToSwap.length !== tileIds.length) {
        console.error("CRITICAL: Some tile IDs for swap not found in rack", { tileIds, rack: myDetails.rack });
        throw new Error("Error de consistencia: Algunas fichas a cambiar no están en tu atril.");
      }

      const newBag = [...state.bag, ...tilesToSwap];
      
      // Shuffle bag
      for (let i = newBag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newBag[i], newBag[j]] = [newBag[j], newBag[i]];
      }
      
      // Draw new tiles
      const drawnTiles = newBag.splice(0, tilesToSwap.length);
      const combinedRack = [...remainingRack, ...drawnTiles];
      
      // Deduplicate rack just in case
      const finalRackIds = new Set();
      const deduplicatedRack: Tile[] = [];
      combinedRack.forEach(t => {
        if (!finalRackIds.has(t.id)) {
          deduplicatedRack.push(t);
          finalRackIds.add(t.id);
        }
      });
      
      const newRack = deduplicatedRack;
      const exchangeEndsTurn = getTileExchangeEndsTurn(state, userId);
      const opponentId = state.players.find(id => id !== userId) || userId;
      
      const newTurn: GameTurn = {
        turnNumber: state.turnCount + 1,
        playerId: userId,
        startedAt: Timestamp.fromDate(new Date(state.updatedAt)),
        finishedAt: Timestamp.now(),
        durationSec: (Date.now() - new Date(state.updatedAt).getTime()) / 1000,
        actionType: 'swap',
        wordsPlayed: [],
        validWords: [],
        invalidWords: [],
        lettersPlaced: [],
        lettersCount: tileIds.length,
        score: 0,
        usedAllRack: false,
        usedPremium: false,
        premiumSquaresUsed: [],
        longestWordLength: 0,
        openedBoard: false,
        blockedBoard: false
      };

      const updateData: any = {
        bag: newBag,
        [`playerDetails.${userId}.rack`]: newRack,
        [`swapsUsed.${userId}`]: (state.swapsUsed?.[userId] || 0) + 1,
        updatedAt: new Date().toISOString(),
        lastMove: {
          userId,
          type: 'swap',
          tiles: tilesToSwap,
          timestamp: new Date().toISOString(),
          id: Math.random().toString(36).substring(7)
        },
        turns: arrayUnion(newTurn)
      };

      if (exchangeEndsTurn) {
        updateData.currentTurn = opponentId;
        updateData.consecutivePasses = 0;
        updateData.turnCount = (state.turnCount || 0) + 1;
      }

      if (timeRemaining !== undefined) {
        updateData[`playerDetails.${userId}.timeRemaining`] = timeRemaining;
      }

      await updateDoc(gameRef, updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
      throw error;
    }
  },

  setLanguage: async (gameId, language) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, { language });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${gameId}`);
    }
  },

  fetchActiveGames: (userId: string) => {
    if (!auth.currentUser) {
      console.warn('fetchActiveGames called but auth.currentUser is null');
      return () => {};
    }

    try {
      set({ activeGamesLoaded: false, activeGamesUserId: userId });

      const q = query(
        collection(db, 'games'),
        where('players', 'array-contains', userId),
        where('status', 'in', ['waiting', 'active'])
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const games = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GameState));
        set({ activeGames: games, activeGamesLoaded: true, activeGamesUserId: userId });
      }, (error) => {
        // If it's a permission error and user is null, it's likely a logout race condition
        if (error.message.includes('permission') && !auth.currentUser) {
          return;
        }
        handleFirestoreError(error, OperationType.LIST, 'games');
      });

      return unsubscribe;
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'games');
      return () => {};
    }
  },

  fetchGameHistory: async (userId: string) => {
    if (!auth.currentUser) {
      console.warn('fetchGameHistory called but auth.currentUser is null');
      return [];
    }

    try {
      const q = query(
        collection(db, 'games'),
        where('players', 'array-contains', userId),
        where('status', '==', 'finished'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GameState));
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'games');
      return [];
    }
  }
}));

function convertToGameRecord(state: GameState): GameRecord {
  const p1Id = state.players?.[0] || 'unknown';
  const p2Id = state.players?.[1] || 'ROBOT';

  return {
    id: state.id,
    createdAt: Timestamp.fromDate(new Date(state.createdAt)),
    startedAt: Timestamp.fromDate(new Date(state.startedAt || state.createdAt)),
    finishedAt: state.finishedAt ? Timestamp.fromDate(new Date(state.finishedAt)) : undefined,
    status: state.status as any,
    language: state.language,
    boardType: state.boardType,
    bagType: state.mode,
    speedType: state.timeLimit ? 'fast' : 'normal',
    player1Id: p1Id,
    player2Id: p2Id,
    opponentType: p2Id === 'ROBOT' ? 'robot' : 'human',
    robotLevel: p2Id === 'ROBOT' ? (state.botDifficulty as any) : undefined,
    winnerId: state.winner === 'draw' ? undefined : state.winner,
    loserId: state.loser,
    isDraw: state.isDraw || state.winner === 'draw',
    endReason: state.endReason || 'normal',
    player1Score: state.playerDetails[p1Id]?.score || 0,
    player2Score: state.playerDetails[p2Id]?.score || 0,
    totalTurns: state.turnCount,
    totalDurationSec: state.finishedAt ? (new Date(state.finishedAt).getTime() - new Date(state.startedAt || state.createdAt).getTime()) / 1000 : 0,
    turns: state.turns || []
  };
}
